import { useRef, useState, type DragEvent } from 'react'
import { Film, GripVertical, Image as ImageIcon, Trash2, UploadCloud } from 'lucide-react'
import { mediaUrl } from '../lib/api'
import { clock, cx, fileSize } from '../lib/format'
import type { Evidence } from '../lib/types'
import { Spinner } from './ui'

const ACCEPT = 'video/*,image/*,.mov,.mp4,.m4v,.heic,.heif,.pdf'
const VIDEO_NAME = /\.(mp4|m4v|mov|qt|avi|mkv|webm|mpeg|mpg|3gp)$/i
const IMAGE_NAME = /\.(jpe?g|png|gif|webp|heic|heif|avif|bmp|tif{1,2})$/i

export function Dropzone({
  onFiles,
  uploading,
  progress,
  disabled,
}: {
  onFiles: (files: File[]) => void
  uploading?: boolean
  progress?: number
  disabled?: boolean
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [dragging, setDragging] = useState(false)
  const [dropHint, setDropHint] = useState<string | null>(null)

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setDragging(false)
    if (disabled) return
    const files = await filesFromDrop(event)
    if (!files.length) {
      setDropHint(
        'Could not read a video from that drop. On a Mac, drag the .mp4 / .mov file from Finder (not Photos), or use Choose files so the real clip is sent.',
      )
      return
    }
    setDropHint(null)
    onFiles(files)
  }

  return (
    <div
      onDragEnter={(event) => {
        event.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragOver={(event) => {
        event.preventDefault()
        event.dataTransfer.dropEffect = 'copy'
        if (!disabled) setDragging(true)
      }}
      onDragLeave={(event) => {
        if (event.currentTarget.contains(event.relatedTarget as Node)) return
        setDragging(false)
      }}
      onDrop={handleDrop}
      className={cx(
        'relative rounded-xl border-2 border-dashed px-6 py-10 text-center transition',
        dragging
          ? 'border-altair-500 bg-altair-500/[0.07]'
          : 'border-white/[0.12] bg-white/[0.015] hover:border-white/25',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files || [])
          if (files.length) onFiles(files)
          event.target.value = ''
        }}
      />

      {uploading ? (
        <div className="flex flex-col items-center">
          <Spinner size={24} className="text-altair-400" />
          <div className="mt-3 text-[13.5px] font-medium text-slate-200">
            Uploading evidence… {Math.round((progress ?? 0) * 100)}%
          </div>
          <div className="mt-3 h-1.5 w-full max-w-sm overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-altair-500 transition-[width] duration-200"
              style={{ width: `${Math.round((progress ?? 0) * 100)}%` }}
            />
          </div>
          <p className="mt-2 text-[11.5px] text-slate-500">
            Large dashcam clips can take a minute. Keep this tab open.
          </p>
        </div>
      ) : (
        <>
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl border border-white/[0.09] bg-white/[0.03] text-altair-400">
            <UploadCloud size={22} strokeWidth={1.9} />
          </div>
          <p className="text-[14.5px] font-semibold text-slate-100">
            Drop dashcam video and photos here
          </p>
          <p className="mx-auto mt-1.5 max-w-md text-[12.5px] leading-relaxed text-slate-500">
            MP4, MOV, JPG, PNG, HEIC or PDF · up to 2 GB per file. Originals are kept unedited and
            checksummed. Drop the .mp4 / .mov file itself — not a Photos tile, Finder preview, or
            iCloud placeholder.
          </p>
          <button type="button" className="btn-ghost mt-4" onClick={() => inputRef.current?.click()}>
            Choose files
          </button>
          {dropHint ? (
            <p className="mx-auto mt-3 max-w-md text-[12.5px] leading-relaxed text-amber-300">
              {dropHint}
            </p>
          ) : null}
        </>
      )}
    </div>
  )
}

export function EvidenceList({
  caseId,
  evidence,
  onRemove,
  onCaption,
  onSource,
  onReorder,
  onFiles,
}: {
  caseId: string
  evidence: Evidence[]
  onRemove?: (id: string) => void
  onCaption?: (id: string, caption: string) => void
  onSource?: (id: string, source: string) => void
  onReorder?: (ids: string[]) => void
  onFiles?: (files: File[]) => void
}) {
  const [dragId, setDragId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | null>(null)

  if (!evidence.length) return null

  const move = (fromId: string, toId: string) => {
    if (!onReorder || fromId === toId) return
    const ids = evidence.map((item) => item.id)
    const from = ids.indexOf(fromId)
    const to = ids.indexOf(toId)
    if (from < 0 || to < 0) return
    ids.splice(from, 1)
    ids.splice(to, 0, fromId)
    onReorder(ids)
  }

  const acceptOsFiles = (event: DragEvent) => {
    if (osFileDrop(event, Boolean(dragId))) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'
      return true
    }
    return false
  }

  const handleListDrop = (event: DragEvent, targetId?: string) => {
    event.preventDefault()
    event.stopPropagation()
    void (async () => {
      if (onFiles && osFileDrop(event, Boolean(dragId))) {
        const files = await filesFromDrop(event)
        setDragId(null)
        setOverId(null)
        if (files.length) onFiles(files)
        return
      }
      const from =
        dragId ||
        event.dataTransfer.getData('application/x-altair-evidence') ||
        event.dataTransfer.getData('text/plain')
      if (from && targetId) move(from, targetId)
      setDragId(null)
      setOverId(null)
    })()
  }

  return (
    <ul
      className="space-y-2"
      onDragOver={(event) => {
        if (acceptOsFiles(event)) return
      }}
      onDrop={(event) => handleListDrop(event)}
    >
      {evidence.map((item) => (
        <li
          key={item.id}
          onDragOver={(event) => {
            if (acceptOsFiles(event)) return
            if (!onReorder || !dragId) return
            event.preventDefault()
            event.dataTransfer.dropEffect = 'move'
            setOverId(item.id)
          }}
          onDrop={(event) => handleListDrop(event, item.id)}
          className={cx(
            'flex items-start gap-3 rounded-lg border bg-ink-900/60 p-2.5',
            dragId === item.id && 'opacity-50',
            overId === item.id && dragId !== item.id
              ? 'border-altair-500/70'
              : 'border-white/[0.07]',
          )}
        >
          {onReorder ? (
            <span
              draggable
              onDragStart={(event) => {
                setDragId(item.id)
                event.dataTransfer.effectAllowed = 'move'
                try {
                  event.dataTransfer.setData('application/x-altair-evidence', item.id)
                } catch {
                  /* Safari only allows a few MIME types on drag data. */
                }
                event.dataTransfer.setData('text/plain', item.id)
              }}
              onDragEnd={() => {
                setDragId(null)
                setOverId(null)
              }}
              className="mt-4 shrink-0 cursor-grab text-slate-600 active:cursor-grabbing"
              title="Drag to reorder"
              aria-hidden
            >
              <GripVertical size={15} />
            </span>
          ) : null}
          <div className="relative h-14 w-20 shrink-0 overflow-hidden rounded bg-black">
            {item.kind === 'video' && item.poster ? (
              <img
                src={mediaUrl(caseId, item.poster)}
                alt=""
                draggable={false}
                className="h-full w-full object-cover opacity-90"
              />
            ) : item.kind === 'photo' ? (
              <img
                src={mediaUrl(caseId, item.webFile || item.file)}
                alt=""
                draggable={false}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-slate-600">
                <Film size={18} />
              </div>
            )}
            <span className="absolute left-1 top-1 rounded bg-black/70 px-1 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-white/85">
              {item.kind === 'video' ? (
                <Film size={9} className="inline" />
              ) : (
                <ImageIcon size={9} className="inline" />
              )}
            </span>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-[13px] font-medium text-slate-200">
                  {item.originalName}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-2.5 text-[11px] text-slate-500">
                  <span>{fileSize(item.size)}</span>
                  {item.durationSec ? <span>{clock(item.durationSec)}</span> : null}
                  {item.width ? (
                    <span>
                      {item.width}×{item.height}
                    </span>
                  ) : null}
                  {item.sha256 ? (
                    <span className="font-mono" title={`SHA-256 ${item.sha256}`}>
                      #{item.sha256.slice(0, 8)}
                    </span>
                  ) : null}
                </div>
              </div>
              {onRemove ? (
                <button
                  type="button"
                  onClick={() => onRemove(item.id)}
                  className="btn-subtle shrink-0 p-1.5 text-slate-500 hover:text-red-300"
                  title="Remove file"
                >
                  <Trash2 size={14} />
                </button>
              ) : null}
            </div>

            {item.kind === 'video' && onSource ? (
              <input
                key={`${item.id}-source-${item.source}`}
                defaultValue={item.source}
                onBlur={(event) => {
                  if (event.target.value !== item.source) onSource(item.id, event.target.value)
                }}
                placeholder="Capture device — e.g. Dashcam, phone"
                className="input input-sm mt-2"
              />
            ) : null}

            {onCaption ? (
              <input
                defaultValue={item.caption}
                onBlur={(event) => {
                  if (event.target.value !== item.caption) onCaption(item.id, event.target.value)
                }}
                placeholder="Add a caption — what does this file show?"
                className="input input-sm mt-2"
              />
            ) : null}
          </div>
        </li>
      ))}
    </ul>
  )
}

function osFileDrop(event: DragEvent, draggingInternal = false) {
  if (draggingInternal) return false
  const types = Array.from(event.dataTransfer?.types || [])
  if (types.includes('application/x-altair-evidence')) return false
  return types.includes('Files')
}

function isVideoFile(file: File) {
  return file.type.startsWith('video/') || VIDEO_NAME.test(file.name)
}

function isImageFile(file: File) {
  return file.type.startsWith('image/') || IMAGE_NAME.test(file.name)
}

function isFinderPreview(file: File) {
  // Finder Quick Look / Photos often attach a JPEG of the first frame. It may
  // keep the .mp4 name with an image type, or be a small still next to the clip.
  if (isVideoFile(file) && isImageFile(file) && file.size < 8 * 1024 * 1024) return true
  return isImageFile(file) && !isVideoFile(file) && file.size <= 256 * 1024
}

function fileKey(file: File) {
  return `${file.name}:${file.size}:${file.lastModified}`
}

async function filesFromDrop(event: DragEvent): Promise<File[]> {
  const transfer = event.dataTransfer
  if (!transfer) return []

  const items = Array.from(transfer.items || [])
  const fromItems: File[] = []
  if (items.length) {
    const batches = await Promise.all(
      items
        .filter((item) => item.kind === 'file')
        .map(async (item) => {
          const asFile = item.getAsFile()
          const entry = item.webkitGetAsEntry?.()
          const nested = entry ? await readEntry(entry) : []
          return pickBestDropFiles([...(asFile ? [asFile] : []), ...nested])
        }),
    )
    fromItems.push(...batches.flat())
  }

  const collected = pickBestDropFiles([...fromItems, ...Array.from(transfer.files || [])])
  const seen = new Set<string>()
  const unique = collected.filter((file) => {
    const key = fileKey(file)
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
  return filterDropFiles(unique)
}

function pickBestDropFiles(files: File[]) {
  const usable = files.filter((file) => file && file.size > 0 && file.name && file.name !== '.')
  const videos = usable.filter((file) => isVideoFile(file) && !isFinderPreview(file))
  return videos.length ? videos : usable
}

function filterDropFiles(files: File[]) {
  const usable = files.filter((file) => file && file.size > 0 && file.name && file.name !== '.')
  const videos = usable.filter((file) => isVideoFile(file) && !isFinderPreview(file))
  const previewOnly = usable.length > 0 && usable.every(isFinderPreview)
  // Finder sometimes drops only the drag-preview JPEG. Treat that as a failed drop
  // so the reporter gets a hint instead of a still that is not the video.
  if (previewOnly) return []
  if (videos.length) return videos
  return usable.filter((file) => !isFinderPreview(file))
}

function readEntry(entry: FileSystemEntry): Promise<File[]> {
  if (entry.isFile) {
    return new Promise((resolve) => {
      ;(entry as FileSystemFileEntry).file(
        (file) => resolve([file]),
        () => resolve([]),
      )
    })
  }
  if (entry.isDirectory) return readDirectory(entry as FileSystemDirectoryEntry)
  return Promise.resolve([])
}

function readDirectory(directory: FileSystemDirectoryEntry): Promise<File[]> {
  return new Promise((resolve) => {
    const reader = directory.createReader()
    const all: File[] = []
    const pump = () => {
      reader.readEntries(async (entries) => {
        if (!entries.length) {
          resolve(all)
          return
        }
        const nested = await Promise.all(entries.map((entry) => readEntry(entry)))
        all.push(...nested.flat())
        pump()
      }, () => resolve(all))
    }
    pump()
  })
}
